import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { NAV, type NavItem } from "@/lib/nav";
import { LogOutIcon } from "@/lib/icons";
import { AccountSwitcher } from "./AccountSwitcher";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";

const STORAGE_KEY = "tw-pulse.sidebar.collapsed";
const EXPANDED_W = "w-[244px]";
const COLLAPSED_W = "w-[60px]";

export function Sidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  return (
    <aside
      className={
        "sticky top-0 z-30 flex h-screen flex-col border-r border-zinc-200 bg-white/90 backdrop-blur-md transition-[width] duration-150 " +
        "dark:border-zinc-800/80 dark:bg-zinc-950/85 " +
        (collapsed ? COLLAPSED_W : EXPANDED_W)
      }
      aria-label="Primary"
    >
      <div
        className={
          "flex h-14 items-center border-b border-zinc-200 px-3 dark:border-zinc-800/80 " +
          (collapsed ? "justify-center" : "")
        }
      >
        <Logo markOnly={collapsed} />
      </div>

      <div className="border-b border-zinc-200 dark:border-zinc-800/80">
        <AccountSwitcher collapsed={collapsed} />
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {NAV.map((section, i) => (
          <Section key={i} section={section} collapsed={collapsed} />
        ))}
      </nav>

      <div className="border-t border-zinc-200 dark:border-zinc-800/80">
        <UserBlock collapsed={collapsed} />
        <CollapseToggle collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------

function Section({
  section,
  collapsed,
}: {
  section: { heading?: string; items: NavItem[] };
  collapsed: boolean;
}) {
  return (
    <div className="mb-4 last:mb-0">
      {section.heading && !collapsed && (
        <div className="px-2 pb-1.5 pt-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-500">
          {section.heading}
        </div>
      )}
      {section.heading && collapsed && (
        <div className="mx-2 mb-1.5 mt-1 h-px bg-zinc-200 dark:bg-zinc-800/80" aria-hidden="true" />
      )}
      <ul className="space-y-0.5">
        {section.items.map((item) => (
          <Row key={item.label} item={item} collapsed={collapsed} />
        ))}
      </ul>
    </div>
  );
}

function Row({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const hasChildren = !!item.children?.length;
  const isActive = !!item.to && location.pathname === item.to;
  const childActive = useMemo(
    () => !!item.children?.some((c) => c.to && location.pathname === c.to),
    [item.children, location.pathname],
  );

  // Auto-expand when a descendant route is active.
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  // Top-level item: leaf
  if (!hasChildren) {
    return (
      <li>
        <Leaf item={item} collapsed={collapsed} />
      </li>
    );
  }

  // Top-level item: parent with children
  return (
    <li>
      <button
        type="button"
        onClick={() => !collapsed && setOpen((o) => !o)}
        title={collapsed ? item.label : undefined}
        aria-expanded={open}
        className={
          "group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition " +
          (isActive || childActive
            ? "bg-zinc-100 text-zinc-900 dark:bg-zinc-800/70 dark:text-zinc-50"
            : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-100") +
          (collapsed ? " justify-center" : "")
        }
      >
        <span className="flex h-4 w-4 items-center justify-center">{item.icon}</span>
        {!collapsed && (
          <>
            <span className="flex-1 truncate text-left">{item.label}</span>
            <Chevron open={open} />
          </>
        )}
      </button>
      {!collapsed && open && (
        <ul className="ml-4 mt-1 space-y-0.5 border-l border-zinc-200 pl-2 dark:border-zinc-800/80">
          {item.children!.map((child) => (
            <li key={child.label}>
              <Leaf item={child} collapsed={false} nested />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Leaf({
  item,
  collapsed,
  nested = false,
}: {
  item: NavItem;
  collapsed: boolean;
  nested?: boolean;
}) {
  const sharedClasses =
    "group flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition " +
    (collapsed ? "justify-center" : "");

  if (item.disabled || !item.to) {
    return (
      <span
        title={collapsed ? `${item.label}${item.badge ? ` · ${item.badge}` : ""}` : undefined}
        aria-disabled="true"
        className={
          sharedClasses +
          " cursor-not-allowed text-zinc-400 hover:bg-transparent dark:text-zinc-600"
        }
      >
        <span
          className={
            "flex h-4 w-4 items-center justify-center " +
            (nested ? "text-zinc-300 dark:text-zinc-700" : "")
          }
        >
          {item.icon}
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {item.badge && <Badge>{item.badge}</Badge>}
          </>
        )}
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        sharedClasses +
        (isActive
          ? " bg-zinc-100 text-zinc-900 dark:bg-zinc-800/70 dark:text-zinc-50"
          : " text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-100")
      }
    >
      <span className="flex h-4 w-4 items-center justify-center">{item.icon}</span>
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && <Badge>{item.badge}</Badge>}
        </>
      )}
    </NavLink>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-zinc-200 bg-zinc-50 px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wider text-zinc-600 dark:border-zinc-700/80 dark:bg-zinc-900 dark:text-zinc-400">
      {children}
    </span>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 20 20"
      aria-hidden="true"
      className={"transition-transform " + (open ? "rotate-90" : "")}
    >
      <path
        d="M7 5l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// User block (bottom of sidebar) — replaces the old AppShell user menu.
// ---------------------------------------------------------------------------

function UserBlock({ collapsed }: { collapsed: boolean }) {
  const { me, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!me) return null;

  return (
    <div ref={wrapRef} className="relative p-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? (me.user.name ?? me.user.email) : undefined}
        className={
          "flex w-full items-center gap-2 rounded-md p-1.5 text-left transition hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " +
          "dark:hover:bg-zinc-800/60 " +
          (collapsed ? "justify-center" : "")
        }
      >
        <Avatar user={me.user} />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] font-medium text-zinc-900 dark:text-zinc-100">
              {me.user.name ?? me.user.email}
            </div>
            <div className="truncate text-[11px] text-zinc-500 dark:text-zinc-500">
              {me.tenant.name}
            </div>
          </div>
        )}
        {!collapsed && (
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            aria-hidden="true"
            className="text-zinc-400 dark:text-zinc-500"
          >
            <path
              d="M5 8l5-5 5 5M5 12l5 5 5-5"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className={
            "absolute z-40 w-60 overflow-hidden rounded-lg border border-zinc-200 bg-white py-1 shadow-card-light " +
            "dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-card dark:backdrop-blur " +
            (collapsed ? "bottom-2 left-full ml-2" : "bottom-full left-2 right-2 mb-2 w-auto")
          }
        >
          <div className="px-3 py-3">
            <div className="truncate text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
              {me.user.name ?? me.user.email}
            </div>
            <div className="mt-0.5 truncate text-[12px] text-zinc-600 dark:text-zinc-400">
              {me.user.email}
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10.5px] uppercase tracking-wider text-zinc-600 dark:border-zinc-800 dark:bg-zinc-950/70 dark:text-zinc-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
              Owner · {me.tenant.name}
            </div>
          </div>
          <div className="my-1 h-px bg-zinc-200 dark:bg-zinc-800" />
          <div className="px-3 py-2">
            <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
              Theme
            </div>
            <ThemeToggle />
          </div>
          <div className="my-1 h-px bg-zinc-200 dark:bg-zinc-800" />
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-zinc-700 transition hover:bg-zinc-100 hover:text-zinc-900 focus-visible:bg-zinc-100 focus-visible:outline-none dark:text-zinc-300 dark:hover:bg-zinc-800/70 dark:hover:text-zinc-50 dark:focus-visible:bg-zinc-800/70"
          >
            <LogOutIcon size={14} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Avatar({
  user,
}: {
  user: { name: string | null; email: string; avatarUrl: string | null };
}) {
  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt=""
        className="h-7 w-7 rounded-full object-cover ring-1 ring-zinc-200 dark:ring-zinc-700"
      />
    );
  }
  const initials = (user.name ?? user.email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-[11px] font-semibold text-white ring-1 ring-white/10">
      {initials || "?"}
    </span>
  );
}

// ---------------------------------------------------------------------------

function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      className={
        "flex w-full items-center gap-2 border-t border-zinc-200 px-3 py-2 text-[12px] text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-800 " +
        "dark:border-zinc-800/80 dark:hover:bg-zinc-900/60 dark:hover:text-zinc-300 " +
        (collapsed ? "justify-center" : "")
      }
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 20 20"
        aria-hidden="true"
        className={"transition-transform " + (collapsed ? "rotate-180" : "")}
      >
        <path
          d="M12 5l-5 5 5 5"
          stroke="currentColor"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {!collapsed && <span>Collapse</span>}
    </button>
  );
}
